import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from 'api'
import { queryKeys, InvoicesListResponse } from 'api/queryKeys'
import { Invoice } from 'types'
import { UseUpdateInvoiceFieldsParams } from '../types'

const useUpdateInvoiceFields = ({ fields, onSuccess, onError }: UseUpdateInvoiceFieldsParams) => {
  const client = useApi()
  const queryClient = useQueryClient()

  const { mutate, isPending } = useMutation({
    mutationFn: async (invoice: Invoice) => {
      const response = await client.putInvoice(
        { id: Number(invoice.id) },
        { invoice: { id: Number(invoice.id), ...fields } }
      )
      return response.data
    },

    // Fires before the request — write optimistic state into the cache immediately
    // so the UI reflects the change without waiting for the server.
    onMutate: async (invoice: Invoice) => {
      // Cancel any in-flight refetches so they don't overwrite the optimistic values
      // we're about to write.
      await queryClient.cancelQueries({ queryKey: queryKeys.invoices.all() })
      await queryClient.cancelQueries({ queryKey: queryKeys.invoice.detail(String(invoice.id)) })

      // Snapshot current cache state — this is the rollback point if the request fails.
      const previousLists = queryClient.getQueriesData<InvoicesListResponse>({ queryKey: queryKeys.invoices.all() })
      const previousDetail = queryClient.getQueryData<Invoice>(queryKeys.invoice.detail(String(invoice.id)))

      // Write the new field values into every cached list page that contains this invoice.
      queryClient.setQueriesData<InvoicesListResponse>(
        { queryKey: queryKeys.invoices.all() },
        (old) => {
          if (!old?.data?.invoices) return old
          return {
            ...old,
            data: {
              ...old.data,
              invoices: old.data.invoices.map((inv) =>
                inv.id === invoice.id ? { ...inv, ...fields } : inv
              ),
            },
          }
        }
      )

      // Also update the detail cache so the show page reflects the change instantly.
      queryClient.setQueryData<Invoice>(
        queryKeys.invoice.detail(String(invoice.id)),
        (old) => (old ? { ...old, ...fields } : old)
      )

      // Return the snapshots so onError can access them for rollback.
      return { previousLists, previousDetail }
    },

    // Server rejected the mutation — rewind both caches to the snapshots taken in onMutate.
    onError: (error, invoice, context) => {
      context?.previousLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      queryClient.setQueryData(
        queryKeys.invoice.detail(String(invoice.id)),
        context?.previousDetail
      )
      onError?.(error as Error)
    },

    onSuccess,

    // Runs after both success and error — reconcile optimistic state with the server's
    // ground truth by triggering a background refetch on both caches.
    onSettled: (_data, _error, invoice) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all() })
      queryClient.invalidateQueries({ queryKey: queryKeys.invoice.detail(String(invoice.id)) })
    },
  })

  return { mutate, isPending }
}

export default useUpdateInvoiceFields
